import { memo, useCallback, useRef, useState, type FC, type RefObject } from "react";
import { message } from "antd";
import type { EditorRef } from "react-email-editor";
import { useTranslation } from "react-i18next";
import type { RecommendedProduct } from "src/services/CampaignAPI";
import CampaignStepCard from "../../../../shared/CampaignStepCard";
import EmailCanvas from "../components/EmailCanvas";
import RecommendedProducts from "../components/RecommendedProducts";
import type { GeneratedTemplate } from "../AiCampaignTypes";
import { insertDesignRows } from "../EmailEditorUtils";
import type { RecommendedProductsState } from "../hooks/UseRecommendedProducts";

interface CreateCampaignStepProps {
  title: string;
  subtitle: string;
  editorRef: RefObject<EditorRef>;
  template: GeneratedTemplate | null;
  recommendedProducts: RecommendedProductsState;
  companyName?: string;
}

const CreateCampaignStep: FC<CreateCampaignStepProps> = ({
  title,
  subtitle,
  editorRef,
  template,
  recommendedProducts,
  companyName,
}) => {
  const { t } = useTranslation();
  const [isCanvasExpanded, setIsCanvasExpanded] = useState(false);
  const isInsertingRef = useRef(false);
  const { gridRows } = recommendedProducts;

  const expandCanvas = useCallback(() => setIsCanvasExpanded(true), []);
  const collapseCanvas = useCallback(() => setIsCanvasExpanded(false), []);

  const insertRows = useCallback(
    async (rows: unknown[]) => {
      if (isInsertingRef.current) {
        return;
      }
      isInsertingRef.current = true;
      try {
        const isInserted = await insertDesignRows(editorRef.current?.editor, rows);
        if (isInserted) {
          message.success(t("campaignFlow.aiWizard.recommendedProducts.inserted"));
        } else {
          message.error(t("campaignFlow.aiWizard.recommendedProducts.insertFailed"));
        }
      } finally {
        isInsertingRef.current = false;
      }
    },
    [editorRef, t]
  );

  const insertProduct = useCallback(
    (product: RecommendedProduct) => insertRows([product.unlayer_row]),
    [insertRows]
  );

  const insertAllProducts = useCallback(() => insertRows(gridRows), [gridRows, insertRows]);

  return (
    <CampaignStepCard
      wide
      title={title}
      subtitle={subtitle}
      headerAction={
        <button type="button" className="create-campaign-step__expand" onClick={expandCanvas}>
          {t("campaignFlow.aiWizard.createCampaign.expandCanvas")}
        </button>
      }
    >
      <div className="create-campaign-step__workspace">
        <EmailCanvas
          editorRef={editorRef}
          template={template}
          isExpanded={isCanvasExpanded}
          onCollapse={collapseCanvas}
        />
        <RecommendedProducts
          state={recommendedProducts}
          brandName={companyName}
          onInsertProduct={insertProduct}
          onInsertAll={gridRows.length ? insertAllProducts : undefined}
        />
      </div>
    </CampaignStepCard>
  );
};

export default memo(CreateCampaignStep);
